/**
 * Edit Service Facility Modal
 *
 * Modal for creating or editing a service facility location.
 */

import { useEffect, useState } from 'react';
import { Close as CloseIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@mui/material';
import type { ServiceFacilityResponse2 } from '@store/generated/billing';
import { useSnackbar } from 'notistack';

import { useServiceFacilityOperations } from '../store';
import { extractServiceFacilityApiError } from '../utils';

import {
  initialServiceFacilityFormData,
  ServiceFacilityForm,
  type ServiceFacilityFormData,
} from './ServiceFacilityForm';

interface EditServiceFacilityModalProps {
  open: boolean;
  onClose: () => void;
  /** Pass null to create a new facility */
  facility: ServiceFacilityResponse2 | null;
  onSaved: () => void;
}

function facilityToFormData(facility: ServiceFacilityResponse2): ServiceFacilityFormData {
  return {
    display_name: facility.display_name || '',
    address: {
      line1: facility.address?.line1 || '',
      line2: facility.address?.line2 || '',
      city: facility.address?.city || '',
      state: facility.address?.state || '',
      zip: facility.address?.zip || '',
    },
    facility_npi: facility.facility_npi || '',
    facility_other_id: facility.facility_other_id || '',
  };
}

export function EditServiceFacilityModal({
  open,
  onClose,
  facility,
  onSaved,
}: EditServiceFacilityModalProps) {
  const { enqueueSnackbar } = useSnackbar();
  const { create, createLoading, update, updateLoading } = useServiceFacilityOperations();
  const isEditing = !!facility;
  const isSubmitting = createLoading || updateLoading;
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [formData, setFormData] = useState<ServiceFacilityFormData>(
    () => facility ? facilityToFormData(facility) : initialServiceFacilityFormData,
  );

  useEffect(() => {
    setFormData(facility ? facilityToFormData(facility) : initialServiceFacilityFormData);
    setSubmitError(null);
  }, [facility]);

  const handleChange = (field: keyof Omit<ServiceFacilityFormData, 'address'>, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddressChange = (field: keyof ServiceFacilityFormData['address'], value: string) => {
    setFormData(prev => ({
      ...prev,
      address: { ...prev.address, [field]: value },
    }));
  };

  const validate = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (formData.display_name.trim().length < 2) {
      errors.display_name = 'Facility name must be at least 2 characters';
    }
    if (!formData.address.line1.trim()) {
      errors.address = 'Address is required';
    }
    if (!formData.address.city.trim() || !formData.address.state.trim() || !formData.address.zip.trim()) {
      errors.address = 'City, state, and ZIP are required';
    }
    if (formData.facility_npi && !/^\d{10}$/.test(formData.facility_npi)) {
      errors.facility_npi = 'NPI must be exactly 10 digits';
    }
    return errors;
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setSubmitError(Object.values(errors)[0]);
      return;
    }

    const address = {
      line1: formData.address.line1.trim(),
      line2: formData.address.line2.trim() || null,
      city: formData.address.city.trim(),
      state: formData.address.state.trim().toUpperCase(),
      zip: formData.address.zip.trim(),
    };

    try {
      if (isEditing) {
        await update({
          serviceFacilityId: facility.service_facility_id,
          updateServiceFacilityRequest: {
            display_name: formData.display_name.trim(),
            address,
            facility_npi: formData.facility_npi.trim() || null,
            facility_other_id: formData.facility_other_id.trim() || null,
          },
        }).unwrap();
        enqueueSnackbar('Service facility updated', { variant: 'success' });
      } else {
        await create({
          createServiceFacilityRequest: {
            display_name: formData.display_name.trim(),
            address: {
              line1: address.line1,
              line2: address.line2 ?? undefined,
              city: address.city,
              state: address.state,
              zip: address.zip,
            },
            facility_npi: formData.facility_npi.trim() || null,
            facility_other_id: formData.facility_other_id.trim() || null,
          },
        }).unwrap();
        enqueueSnackbar('Service facility created', { variant: 'success' });
      }
      onSaved();
      onClose();
    } catch (err) {
      const errorMessage = extractServiceFacilityApiError(
        err,
        `Failed to ${isEditing ? 'update' : 'create'} service facility.`,
      );
      setSubmitError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error', autoHideDuration: 10000 });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth={"md"}>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>{isEditing ? 'Edit Service Facility' : 'Add Service Facility'}</span>
          <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close" size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <ServiceFacilityForm
          data={formData}
          onChange={handleChange}
          onAddressChange={handleAddressChange}
        />

        {submitError && (
          <Alert severity="error" sx={{ mt: 3 }}>
            {submitError}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : undefined}
        >
          {isSubmitting ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Facility'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
